/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import { Mail, Chrome } from 'lucide-react';
import { useLogin, useNotify } from 'react-admin';

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const login = useLogin();
  const notify = useNotify();

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await login({ provider: 'google' });
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      notify('Login failed. Please try again.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await login({ email, password });
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      notify('Login failed. Please try again.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 3,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Musedrops Admin
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Sign in to access the admin interface
          </Typography>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleEmailLogin}
            sx={{ marginBottom: 2 }}
          >
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail((e.target as HTMLInputElement).value)}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword((e.target as HTMLInputElement).value)}
              margin="normal"
              required
              disabled={loading}
            />
            <Button
              type="submit"
              variant="outlined"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <Mail />}
              disabled={loading || !email || !password}
              sx={{ marginTop: 2, paddingY: 1.5 }}
            >
              {loading ? 'Signing in...' : 'Sign in with Email'}
            </Button>
          </Box>

          <Divider sx={{ marginY: 2 }}>OR</Divider>

          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <Chrome />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{
              paddingY: 1.5,
              backgroundColor: '#4285f4',
              '&:hover': {
                backgroundColor: '#357ae8',
              },
            }}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ marginTop: 2, display: 'block' }}
          >
            Only authorized users can access this application
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
